import { inject, Injectable } from '@angular/core';
import { TenantFacade } from '@rd/core/facades/tenant-facade/tenant-facade';
import { ApiValidationService } from '@rd/core/services/api-validation/api-validation.service';
import { NydusNetworkApiDataService } from '@rd/core/services/nydus-network-api-data/nydus-network-api-data.service';
import {
  autoRedemptionSettingIndexResponseSchema,
  autoRedemptionSettingPostResponseSchema,
  autoRedemptionSettingPutResponseSchema,
} from '@rd/schemas/auto-redemption-settings.schema';
import {
  AutoRedemptionSettingIndexResponse,
  AutoRedemptionSettingPostResponse,
  AutoRedemptionSettingPutResponse,
} from '@rd/types/auto-redemption-setting.response';
import {
  AutoRedemptionSetting,
  AutoRedemptionSettingStrategy,
  AutoRedemptionSettingType,
  PeriodAutoRedemptionSettingStrategyOptions,
} from '@rd/types/auto-redemption-setting.type';
import { NydusRecipient } from '@rd/types/nydus-recipient.type';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

enum PostAutoRedemptionSettingType {
  Cash = 'CashRedemptionSetting',
  PointsTransfer = 'PointsTransferSetting',
  DigitalGiftCard = 'DigitalGiftCardSetting',
  PhysicalGiftCard = 'PhysicalGiftCardSetting',
}

export interface PointsTransferWithMembershipData {
  // Don't need loyalty program id since NN will pull it from the membership
  membership_id: string;
  number: string;
  firstName: string;
  lastName: string;
}

export interface PointsTransferWithOutMembershipData {
  loyalty_program_id: string;
  number: string;
  firstName: string;
  lastName: string;
}

export interface PointsTransferWithOnlyMembershipId {
  // [Hau]: we only need membership id for everyday spend
  // cause we cannot edit the user details for everyday spend
  // unless we unlink and link a new membership so just need to pass in the membership id
  // Also card number is masked so if we pass that to NN, it will trigger fail validation
  membership_id: string;
  // [Hau]: Only need this to show the card number in the review page
  number?: string;
}

export interface CashBackToCardWithMembershipData {
  number: string;
  membership_id: string;
  enrollment_id: string;
  firstName: string;
  lastName: string;
  starting_date?: string;
}

export interface CashBackToCardWithOutMembershipData {
  number: string;
  loyalty_program_id: string;
  enrollment_id: string;
  firstName: string;
  lastName: string;
  starting_date?: string;
}

export interface CashBackToAccountWithMembershipData {
  membership_id: string;
  number: string;
  firstName: string;
  lastName: string;
  details: {
    bsb_number: string;
  };
}

export interface CashBackToAccountWithOutMembershipData {
  loyalty_program_id: string;
  number: string;
  firstName: string;
  lastName: string;
  details: {
    bsb_number: string;
  };
}

// interface
export interface CreatePointsTransferAutoRedemptionSetting {
  product_id: string; // product id so that we can use this to differentiate between loyalty programs and gift cards
  type: AutoRedemptionSettingType.PointsTransfer;
  displayValue: number; // This is what we use in the review page to show
  pointsRequired: number | null;
  data:
    | PointsTransferWithMembershipData
    | PointsTransferWithOutMembershipData
    | PointsTransferWithOnlyMembershipId;
  strategy: AutoRedemptionSettingStrategy;
  strategy_options: PeriodAutoRedemptionSettingStrategyOptions | {};
}

export interface CreateCashBackAutoRedemptionSetting {
  product_id: string; // product id so that we can use this to differentiate between loyalty programs and gift cards
  type: AutoRedemptionSettingType.Cash;
  displayValue: number;
  pointsRequired: number | null;
  data:
    | CashBackToCardWithMembershipData
    | CashBackToCardWithOutMembershipData
    | CashBackToAccountWithMembershipData
    | CashBackToAccountWithOutMembershipData;
  strategy: AutoRedemptionSettingStrategy;
  strategy_options: PeriodAutoRedemptionSettingStrategyOptions | {};
}

export interface GiftCardAutoRedemptionRecipient extends NydusRecipient {
  message: string;
}

export interface CreateDigitalGiftCardAutoRedemptionSetting {
  product_id: string; // product id so that we can use this to differentiate between loyalty programs and gift cards
  type: AutoRedemptionSettingType.DigitalGiftCard;
  displayValue: number;
  pointsRequired: number | null;
  data: {
    card_id: string;
    display_value: number;
    quantity: number | null;
    recipient: Partial<GiftCardAutoRedemptionRecipient>;
  };
  strategy: AutoRedemptionSettingStrategy;
  strategy_options: PeriodAutoRedemptionSettingStrategyOptions | {};
}

export interface CreatePhysicalGiftCardAutoRedemptionSetting {
  product_id: string; // product id so that we can use this to differentiate between loyalty programs and gift cards
  type: AutoRedemptionSettingType.PhysicalGiftCard;
  displayValue: number;
  pointsRequired: number | null;
  data: {
    card_id: string;
    display_value: number;
    quantity: number | null;
    recipient: GiftCardAutoRedemptionRecipient;
  };
  strategy: AutoRedemptionSettingStrategy;
  strategy_options: PeriodAutoRedemptionSettingStrategyOptions | {};
}

@Injectable({
  providedIn: 'root',
})
export class AutoRedemptionSettingApiService {
  private nydusNetworkDataService = inject(NydusNetworkApiDataService);
  private apiValidationService = inject(ApiValidationService);
  private tenantFacade = inject(TenantFacade);

  createAutoRedemptionSetting(
    body:
      | CreatePointsTransferAutoRedemptionSetting
      | CreateCashBackAutoRedemptionSetting
      | CreateDigitalGiftCardAutoRedemptionSetting
      | CreatePhysicalGiftCardAutoRedemptionSetting,
  ): Observable<AutoRedemptionSetting> {
    let pointsRequired = body.pointsRequired;
    switch (body.strategy) {
      case AutoRedemptionSettingStrategy.BeginningOfMonth:
      case AutoRedemptionSettingStrategy.EveryFewMonths:
      case AutoRedemptionSettingStrategy.EndOfMonth: {
        pointsRequired = null;
        break;
      }
    }
    // The reason we handle points Required
    return this.nydusNetworkDataService
      .post<AutoRedemptionSettingPostResponse>('auto_redemption_settings', {
        type: getAutoRedemptionSettingType(body.type),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        data: transformCreateAutoRedemptionSettingData(body),
        strategy: body.strategy,
        pointsRequired,
        strategy_options: body.strategy_options,
      })
      .pipe(
        tap((response) => {
          this.apiValidationService.validate<AutoRedemptionSettingPostResponse>(
            {
              apiName: '(POST) auto_redemption_settings',
              schema: autoRedemptionSettingPostResponseSchema,
              response,
            },
          );
        }),
        map((response) => this.transformPostAndPutResponse(response)),
      );
  }

  updateAutoRedemptionSetting(
    id: string,
    pointsRequired: number,
    options: {
      strategy: AutoRedemptionSettingStrategy;
      strategyOptions: PeriodAutoRedemptionSettingStrategyOptions | {};
    },
  ): Observable<AutoRedemptionSetting> {
    let finalPointsRequired: number | null = pointsRequired;
    switch (options.strategy) {
      case AutoRedemptionSettingStrategy.BeginningOfMonth:
      case AutoRedemptionSettingStrategy.EndOfMonth:
      case AutoRedemptionSettingStrategy.EveryFewMonths: {
        finalPointsRequired = null;
      }
    }
    return this.nydusNetworkDataService
      .put<AutoRedemptionSettingPutResponse>(`auto_redemption_settings/${id}`, {
        auto_redemption_setting: {
          activation: true,
          pointsRequired: finalPointsRequired,
          strategy: options.strategy,
          strategy_options: options.strategyOptions,
        },
      })
      .pipe(
        tap((response) => {
          this.apiValidationService.validate<AutoRedemptionSettingPutResponse>({
            apiName: `(PUT) auto_redemption_settings/${id}`,
            schema: autoRedemptionSettingPutResponseSchema,
            response,
          });
        }),
        map((response) => this.transformPostAndPutResponse(response)),
      );
  }

  deleteAutoRedemptionSetting(id: string): Observable<void> {
    return this.nydusNetworkDataService.delete<void>(
      `auto_redemption_settings/${id}`,
    );
  }

  getAutoRedemptionSettings(): Observable<AutoRedemptionSetting[]> {
    return this.tenantFacade.getTenantFeatureFlags$.pipe(
      filter(
        (featureFlags: { [key: string]: boolean }) =>
          featureFlags['autoRedemption'],
      ),
      switchMap((_) =>
        this.nydusNetworkDataService
          .get<AutoRedemptionSettingIndexResponse>('auto_redemption_settings')
          .pipe(
            tap((response) => {
              this.apiValidationService.validate<AutoRedemptionSettingIndexResponse>(
                {
                  apiName: 'auto_redemption_settings',
                  schema: autoRedemptionSettingIndexResponseSchema,
                  response,
                },
              );
            }),
            map((response) => this.transformResponse(response)),
          ),
      ),
    );
  }

  private transformPostAndPutResponse(
    response: AutoRedemptionSettingPutResponse,
  ): AutoRedemptionSetting {
    const returned = {
      type: response.data.type,
      ...response.data.attributes,
    };

    return returned as AutoRedemptionSetting;
  }

  private transformResponse(
    response: AutoRedemptionSettingIndexResponse,
  ): AutoRedemptionSetting[] {
    const autoRedemptionSettings: AutoRedemptionSetting[] = [];
    response.data.forEach((res) => {
      switch (res.type) {
        case AutoRedemptionSettingType.Cash: {
          autoRedemptionSettings.push({
            type: res.type,
            ...res.attributes,
          });
          break;
        }

        case AutoRedemptionSettingType.PointsTransfer: {
          autoRedemptionSettings.push({
            type: res.type,
            ...res.attributes,
          });
          break;
        }

        case AutoRedemptionSettingType.DigitalGiftCard: {
          autoRedemptionSettings.push({
            type: res.type,
            ...res.attributes,
          });
          break;
        }

        case AutoRedemptionSettingType.PhysicalGiftCard: {
          autoRedemptionSettings.push({
            type: res.type,
            ...res.attributes,
          });
          break;
        }
      }
    });

    return autoRedemptionSettings;
  }
}

// util functions
function getAutoRedemptionSettingType(
  type: AutoRedemptionSettingType,
): PostAutoRedemptionSettingType {
  switch (type) {
    case AutoRedemptionSettingType.PointsTransfer:
      return PostAutoRedemptionSettingType.PointsTransfer;
    case AutoRedemptionSettingType.Cash:
      return PostAutoRedemptionSettingType.Cash;
    case AutoRedemptionSettingType.DigitalGiftCard:
      return PostAutoRedemptionSettingType.DigitalGiftCard;
    case AutoRedemptionSettingType.PhysicalGiftCard:
      return PostAutoRedemptionSettingType.PhysicalGiftCard;
  }
}

function transformCreateAutoRedemptionSettingData(
  body:
    | CreatePointsTransferAutoRedemptionSetting
    | CreateCashBackAutoRedemptionSetting
    | CreateDigitalGiftCardAutoRedemptionSetting
    | CreatePhysicalGiftCardAutoRedemptionSetting,
):
  | PointsTransferWithMembershipData
  | PointsTransferWithOutMembershipData
  | PointsTransferWithOnlyMembershipId
  | CashBackToAccountWithMembershipData
  | CashBackToAccountWithOutMembershipData
  | CashBackToCardWithMembershipData
  | CashBackToCardWithOutMembershipData
  | CreateDigitalGiftCardAutoRedemptionSetting['data']
  | CreatePhysicalGiftCardAutoRedemptionSetting['data']
  | undefined {
  switch (body.type) {
    case AutoRedemptionSettingType.PhysicalGiftCard:
    case AutoRedemptionSettingType.DigitalGiftCard: {
      return body.data;
    }
    case AutoRedemptionSettingType.PointsTransfer: {
      const hasUserDetails = (
        data: CreatePointsTransferAutoRedemptionSetting['data'],
      ): data is
        | PointsTransferWithMembershipData
        | PointsTransferWithOutMembershipData => {
        return 'number' in data && 'firstName' in data && 'lastName' in data;
      };

      if (!hasUserDetails(body.data)) {
        return {
          membership_id: body.data.membership_id,
        };
      }

      const baseData = {
        number: body.data.number,
        firstName: body.data.firstName,
        lastName: body.data.lastName,
      };
      if ('membership_id' in body.data) {
        return {
          ...baseData,
          membership_id: body.data.membership_id,
        };
      }

      if ('loyalty_program_id' in body.data) {
        return {
          ...baseData,
          loyalty_program_id: body.data.loyalty_program_id,
        };
      }
      break;
    }

    case AutoRedemptionSettingType.Cash: {
      // cash back to card with membership
      // no need loyalty program id since nn will pull from membership
      if ('membership_id' in body.data && 'enrollment_id' in body.data) {
        return {
          membership_id: body.data.membership_id,
          enrollment_id: body.data.enrollment_id,
          number: body.data.number,
          firstName: body.data.firstName,
          lastName: body.data.lastName,
        };
      }

      // cashback to account with membership
      // no need loyalty program id since nn will pull from membership
      if ('membership_id' in body.data && !('enrollment_id' in body.data)) {
        const cashbackToAccountWithMembershipData =
          body.data as CashBackToAccountWithMembershipData;
        return {
          membership_id: body.data.membership_id,
          number: cashbackToAccountWithMembershipData.number,
          firstName: cashbackToAccountWithMembershipData.firstName,
          lastName: cashbackToAccountWithMembershipData.lastName,
          details: {
            bsb_number: cashbackToAccountWithMembershipData.details.bsb_number,
          },
        };
      }

      // cashback to account no membership,
      if ('details' in body.data) {
        const cashbackToAccountWithoutMembershipData =
          body.data as CashBackToAccountWithOutMembershipData;
        return {
          loyalty_program_id:
            cashbackToAccountWithoutMembershipData.loyalty_program_id,
          number: cashbackToAccountWithoutMembershipData.number,
          firstName: cashbackToAccountWithoutMembershipData.firstName,
          lastName: cashbackToAccountWithoutMembershipData.lastName,
          details: {
            bsb_number:
              cashbackToAccountWithoutMembershipData.details.bsb_number,
          },
        };
      }

      // Cashback to card no membership
      const data = body.data as CashBackToCardWithOutMembershipData;
      return {
        loyalty_program_id: data.loyalty_program_id,
        number: data.number,
        enrollment_id: data.enrollment_id,
        firstName: data.firstName,
        lastName: data.lastName,
      };
    }
  }
}
